import type { BookingTime, RecurringOccurrences, SingleOccurrence, TimeWindow } from '../../../api/generated';
import { RecurrenceDaysOfWeekEnum } from '../../../api/generated';
import {
  DEFAULT_INTERVAL_WEEKS,
  FULL_DAY_END_TIME,
  FULL_DAY_START_TIME,
  RECURRING_OCCURRENCES_KIND,
  SINGLE_OCCURRENCE_KIND
} from './bookingConstants';

export interface DateTimeParts {
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm, '' when all day
  endTime: string; // HH:mm, '' when all day
  isAllDay: boolean;
}

export interface RecurrenceParts {
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD, '' = open-ended
  startTime: string; // HH:mm, '' when all day
  endTime: string; // HH:mm, '' when all day
  isAllDay: boolean;
  daysOfWeek: RecurrenceDaysOfWeekEnum[];
  intervalWeeks: number;
  exceptionDates: string[]; // YYYY-MM-DD dates skipped from the series
}

// The recurring booking fields a recurring booking is loaded into for editing.
export interface RecurrenceFormState {
  date: string; // occurrenceStart, YYYY-MM-DD
  startTime: string; // HH:mm, '' = all day
  endTime: string; // HH:mm, '' = all day
  isAllDay: boolean;
  daysOfWeek: RecurrenceDaysOfWeekEnum[];
  intervalWeeks: number;
  endDate: string; // YYYY-MM-DD, '' = open-ended
  exceptionDates: string[]; // YYYY-MM-DD
}

// A LocalDateTime string looks like "2026-06-12T09:00" or "2026-06-12T09:00:00".
const DATE_LENGTH = 10; // "YYYY-MM-DD"
const TIME_START = 11;
const TIME_END = 16; // up to and excluding seconds -> "HH:mm"
const TIME_HH_MM_LENGTH = 5; // a LocalTime string is "HH:mm" or "HH:mm:ss"

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

const JS_DAY_TO_ENUM: Record<number, RecurrenceDaysOfWeekEnum> = {
  0: RecurrenceDaysOfWeekEnum.Sunday,
  1: RecurrenceDaysOfWeekEnum.Monday,
  2: RecurrenceDaysOfWeekEnum.Tuesday,
  3: RecurrenceDaysOfWeekEnum.Wednesday,
  4: RecurrenceDaysOfWeekEnum.Thursday,
  5: RecurrenceDaysOfWeekEnum.Friday,
  6: RecurrenceDaysOfWeekEnum.Saturday
};

const toLocalDateTime = (date: string, time: string): string => `${date}T${time}`;

const parseIsoDate = (isoDate: string): Date => new Date(`${isoDate}T00:00`);

const toIsoDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const mondayOf = (date: Date): Date => addDays(date, -((date.getDay() || 7) - 1));

// Rounded to absorb the DST hour shift between two local midnights.
const weeksBetween = (fromMonday: Date, toMonday: Date): number =>
  Math.round((toMonday.getTime() - fromMonday.getTime()) / MS_PER_WEEK);

export const isRecurringTime = (time: BookingTime): time is RecurringOccurrences =>
  time.kind === RECURRING_OCCURRENCES_KIND;

export const isSingleOccurrenceTime = (time: BookingTime): time is SingleOccurrence =>
  time.kind === SINGLE_OCCURRENCE_KIND;

/**
 * TimeWindow times arrive as "HH:mm:ss"; the UI shows "HH:mm". A missing window is a legitimate
 * state meaning all-day (the backend allows it even though this UI always sends a window) and
 * returns null so callers can label it instead of rendering blank times.
 */
export const timeWindowParts = (
  timeWindow: TimeWindow | undefined
): { startTime: string; endTime: string } | null => {
  if (!timeWindow?.start || !timeWindow.end) return null;
  return {
    startTime: timeWindow.start.slice(0, TIME_HH_MM_LENGTH),
    endTime: timeWindow.end.slice(0, TIME_HH_MM_LENGTH)
  };
};

/** The weekday of a YYYY-MM-DD date, or null for an empty/incomplete value. */
export const weekdayOfIsoDate = (isoDate: string): RecurrenceDaysOfWeekEnum | null => {
  if (isoDate.length < DATE_LENGTH) return null;
  const day = parseIsoDate(isoDate).getDay();
  return Number.isNaN(day) ? null : JS_DAY_TO_ENUM[day];
};

/**
 * First date the series actually occurs, mirroring the backend's week-anchored expansion
 * (BookingTimeOverlap): the pattern is anchored to the Mon–Sun week containing the start, and a
 * selected weekday falling earlier in that week than the start itself is not an occurrence.
 * Returns null for incomplete/invalid input, or when the series never occurs within its range —
 * the latter is only possible with an end date, since an open-ended series always reaches its
 * next pattern week.
 */
export const firstOccurrenceDate = (
  startDate: string,
  daysOfWeek: RecurrenceDaysOfWeekEnum[],
  intervalWeeks: number,
  endDate?: string
): string | null => {
  if (startDate.length < DATE_LENGTH || daysOfWeek.length === 0 || intervalWeeks < 1) return null;
  const start = parseIsoDate(startDate);
  const anchorMonday = mondayOf(start);
  // The first occurrence (if any) is in the start week or the next pattern week, intervalWeeks
  // later — the scan never needs to look past the end of that second candidate week.
  const scanEnd = addDays(anchorMonday, intervalWeeks * 7 + 6);
  const end = endDate ? parseIsoDate(endDate) : scanEnd;
  const limit = new Date(Math.min(end.getTime(), scanEnd.getTime()));
  for (let date = start; date <= limit; date = addDays(date, 1)) {
    const isPatternWeek = weeksBetween(anchorMonday, mondayOf(date)) % intervalWeeks === 0;
    if (isPatternWeek && daysOfWeek.includes(JS_DAY_TO_ENUM[date.getDay()])) {
      return toIsoDate(date);
    }
  }
  return null;
};

/**
 * Whether a date is a real occurrence of a recurring pattern: within the (optional) range, on a
 * selected weekday, and in a pattern week. Mirrors the week-anchored expansion of
 * firstOccurrenceDate and the backend's BookingTimeOverlap, so only genuine occurrences can be
 * skipped.
 */
export const isOccurrenceDate = (
  isoDate: string,
  startDate: string,
  daysOfWeek: RecurrenceDaysOfWeekEnum[],
  intervalWeeks: number,
  endDate?: string
): boolean => {
  if (isoDate.length < DATE_LENGTH || startDate.length < DATE_LENGTH) return false;
  if (daysOfWeek.length === 0 || intervalWeeks < 1) return false;
  const date = parseIsoDate(isoDate);
  if (Number.isNaN(date.getTime())) return false;
  const start = parseIsoDate(startDate);
  if (date < start) return false;
  if (endDate && endDate.length >= DATE_LENGTH && date > parseIsoDate(endDate)) return false;
  if (!daysOfWeek.includes(JS_DAY_TO_ENUM[date.getDay()])) return false;
  return weeksBetween(mondayOf(start), mondayOf(date)) % intervalWeeks === 0;
};

/** Drops skipped dates the current pattern/range no longer makes real occurrences (save-time prune). */
export const pruneExceptionDates = (
  exceptionDates: string[],
  startDate: string,
  daysOfWeek: RecurrenceDaysOfWeekEnum[],
  intervalWeeks: number,
  endDate?: string
): string[] =>
  exceptionDates.filter((date) =>
    isOccurrenceDate(date, startDate, daysOfWeek, intervalWeeks, endDate)
  );

/** Whether a stored window spans the whole calendar day — start of day to the end-of-day sentinel. */
export const isFullDayWindow = (startTime: string, endTime: string): boolean =>
  startTime === FULL_DAY_START_TIME && endTime === FULL_DAY_END_TIME;

// A single booking has no way to say "no time", so an all-day one spans the calendar day instead.
export const buildSingleOccurrence = (parts: DateTimeParts): SingleOccurrence => ({
  kind: SINGLE_OCCURRENCE_KIND,
  startDate: toLocalDateTime(parts.date, parts.isAllDay ? FULL_DAY_START_TIME : parts.startTime),
  endDate: toLocalDateTime(parts.date, parts.isAllDay ? FULL_DAY_END_TIME : parts.endTime)
});

// The generated client types `daysOfWeek` as a Set, but the request body is JSON.stringified and
// a real Set serializes to `{}` — so we send a plain array cast through unknown. Skipped dates are
// pruned to the current pattern/range here (save-time prune) so the payload is always valid.
export const buildRecurringOccurrences = (parts: RecurrenceParts): RecurringOccurrences => ({
  kind: RECURRING_OCCURRENCES_KIND,
  occurrenceStart: parts.startDate,
  occurrenceEnd: parts.endDate || undefined,
  // A series can say "all day" honestly, by carrying no window at all — the shape the backend
  // validator names as whole day. No sentinel is needed here.
  timeWindow: parts.isAllDay ? undefined : { start: parts.startTime, end: parts.endTime },
  recurrence: {
    daysOfWeek: parts.daysOfWeek as unknown as Set<RecurrenceDaysOfWeekEnum>,
    intervalWeeks: parts.intervalWeeks
  },
  exceptionDates: pruneExceptionDates(
    parts.exceptionDates,
    parts.startDate,
    parts.daysOfWeek,
    parts.intervalWeeks,
    parts.endDate || undefined
  )
});

// Loads a stored RecurringOccurrences into the form's recurrence fields. `daysOfWeek` is typed as a
// Set but arrives as a plain JSON array; Array.from handles both. A missing timeWindow means all
// day, which sets `isAllDay` and leaves the time fields empty — a series carries no times to
// restore, unlike a single occurrence. The recurrence kind itself is fixed — a recurring booking
// stays recurring on edit.
export const splitRecurringOccurrence = (occurrence: RecurringOccurrences): RecurrenceFormState => {
  const window = timeWindowParts(occurrence.timeWindow);
  return {
    date: (occurrence.occurrenceStart ?? '').slice(0, DATE_LENGTH),
    startTime: window?.startTime ?? '',
    endTime: window?.endTime ?? '',
    // Read from the source rather than from `window`, which also goes null for a half-set window
    // (invalid data the backend validator rejects) — that is not the same thing as all day.
    isAllDay: !occurrence.timeWindow?.start && !occurrence.timeWindow?.end,
    daysOfWeek: Array.from(occurrence.recurrence?.daysOfWeek ?? []),
    intervalWeeks: occurrence.recurrence?.intervalWeeks ?? DEFAULT_INTERVAL_WEEKS,
    endDate: (occurrence.occurrenceEnd ?? '').slice(0, DATE_LENGTH),
    exceptionDates: (occurrence.exceptionDates ?? []).map((date) => date.slice(0, DATE_LENGTH))
  };
};

// Splits a stored SingleOccurrence back into the form's date + start/end time fields. The
// single-day bookings this UI creates share one calendar date, which is always taken from
// startDate; a cross-midnight endDate would keep its time but lose its date.
//
// `isAllDay` reports that the stored window spans the calendar day, but the times are returned
// either way: a form that does not offer all-day still needs a valid window to load and save, and
// one that does hides the fields anyway. Blanking them here would strand the opt-out forms with an
// unsaveable booking.
export const splitSingleOccurrence = (occurrence: SingleOccurrence): DateTimeParts => {
  const start = occurrence.startDate ?? '';
  const end = occurrence.endDate ?? '';
  const startTime = start.slice(TIME_START, TIME_END);
  const endTime = end.slice(TIME_START, TIME_END);
  return {
    date: start.slice(0, DATE_LENGTH),
    startTime,
    endTime,
    isAllDay: isFullDayWindow(startTime, endTime)
  };
};
